import React, { lazy, Suspense } from 'react';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { VisualizationData } from '../../../types/visualization.types';
import { exportVisualizationToExcel } from '../../../utils/excelExport';
import {
  DataVisualizationModal__Overlay,
  DataVisualizationModal__Root,
  DataVisualizationModal__Header,
  DataVisualizationModal__Title,
  DataVisualizationModal__Actions,
  DataVisualizationModal__ExportBtn,
  DataVisualizationModal__CloseBtn,
  DataVisualizationModal__Body,
  DataVisualizationModal__Spinner,
  DataVisualizationModal__Error,
} from './DataVisualizationModal.styles';

/**
 * Lazy-load the Recharts component so it only enters the bundle when the
 * modal is first opened (Recharts + D3 is ~400 KB).
 */
const RechartsChart = lazy(() => import('./RechartsChart'));

interface DataVisualizationModalProps {
  data: VisualizationData;
  onClose: () => void;
}

export const DataVisualizationModal: React.FC<DataVisualizationModalProps> = ({
  data,
  onClose,
}) => {
  const handleExport = () => {
    exportVisualizationToExcel(data, data.title || 'export');
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <DataVisualizationModal__Overlay onClick={handleOverlayClick}>
      <DataVisualizationModal__Root>
        <DataVisualizationModal__Header>
          <DataVisualizationModal__Title>{data.title}</DataVisualizationModal__Title>
          <DataVisualizationModal__Actions>
            <DataVisualizationModal__ExportBtn onClick={handleExport} title="Export to Excel">
              <DownloadOutlined /> Export Excel
            </DataVisualizationModal__ExportBtn>
            <DataVisualizationModal__CloseBtn onClick={onClose} title="Close">
              <CloseOutlined />
            </DataVisualizationModal__CloseBtn>
          </DataVisualizationModal__Actions>
        </DataVisualizationModal__Header>

        <DataVisualizationModal__Body>
          <Suspense fallback={<DataVisualizationModal__Spinner />}>
            {data.traces.length > 0 ? (
              <RechartsChart data={data} />
            ) : (
              <DataVisualizationModal__Error>
                No chart data was returned by the agent.
              </DataVisualizationModal__Error>
            )}
          </Suspense>
        </DataVisualizationModal__Body>
      </DataVisualizationModal__Root>
    </DataVisualizationModal__Overlay>
  );
};
